import React, { useState, useEffect } from 'react';
import { Form, Button, ListGroup } from 'react-bootstrap';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useSelector} from 'react-redux';
import { toast } from 'react-toastify';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import {
  useGetPaymentDetailsQuery,
  useUploadPaymentImagesMutation,
  useUpdatePaymentMutation,
} from '../../slices/paymentsApiSlice';
import { useGetLandlordsQuery } from '../../slices/landlordsApiSlice';

const PaymentEditScreen = () => {
  const { id: paymentId } = useParams();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  // State management
  const [formData, setFormData] = useState({
    date: '',
    account: '',
    accountName: '',
    subAccount: '',
    details: '',
    amount: '',
    category: '',
    notes: '',
    username: '',
  });
  const [images, setImages] = useState([]);
  const [landlordSearch, setLandlordSearch] = useState('');
  const [filteredLandlords, setFilteredLandlords] = useState([]);
  const [selectedLandlord, setSelectedLandlord] = useState(null);

  const { data: payment, isLoading, error } = useGetPaymentDetailsQuery(paymentId);
  const { data: landlordsData } = useGetLandlordsQuery({ keyword: landlordSearch });
  const [updatePayment] = useUpdatePaymentMutation();
  const [uploadPaymentImages] = useUploadPaymentImagesMutation();

  useEffect(() => {
    if (payment) {
      setFormData({
        date: payment.date ? new Date(payment.date).toISOString().split('T')[0] : '',
        account: payment.account || '',
        accountName: payment.accountName || '',
        subAccount: payment.subAccount || '',
        details: payment.details || '',
        amount: payment.amount || '',
        category: payment.category || '',
        notes: payment.notes || '',
        username: payment.username || '',
      });
      setImages(payment.images || []);
    }
  }, [payment]);

  useEffect(() => {
    if (landlordSearch.length >= 3 && landlordsData?.landlords) {
      setFilteredLandlords(
        landlordsData.landlords.filter((landlord) =>
          landlord.name.toLowerCase().includes(landlordSearch.toLowerCase())
        )
      );
    } else {
      setFilteredLandlords([]);
    }
  }, [landlordSearch, landlordsData]);

  const handleLandlordSelect = (landlord) => {
    setSelectedLandlord(landlord);
    setFormData((prevData) => ({
      ...prevData,
      accountName: landlord.name,
    }));
    setFilteredLandlords([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (name === 'accountName') setLandlordSearch(value);
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      await updatePayment({
        paymentId,
        ...formData,
        images,
        accountName: selectedLandlord?.name || formData.accountName, 
        username: userInfo.name || '', // Add the current user's name
      }).unwrap();
      toast.success('Payment updated successfully');
      navigate('/admin/paymentlist');
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const validateFiles = (files) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`File type not allowed: ${file.type}`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`File size exceeds 5MB: ${file.name}`);
        return false;
      }
    }
    return true;
  };

  const uploadImageHandler = async (files) => {
    if (!validateFiles(files)) return;

    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append('images', file));

    try {
      const res = await uploadPaymentImages(formData).unwrap();
      toast.success(res.message);
      setImages([...images, ...res.images]);
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const removeImageHandler = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  return (
    <div className="container mt-4">
      <h1>Edit Payment</h1>
      <Link to='/admin/paymentlist' className='btn btn-light my-3'>
              Go Back
      </Link>
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'Error loading payment details'}
        </Message>
      ) : (
        <Form onSubmit={submitHandler}>
          <Form.Group controlId="date" className="mb-3">
            <Form.Label>Date</Form.Label>
            <Form.Control
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
            />
          </Form.Group>
  
          <Form.Group controlId="account" className="mb-3">
            <Form.Label>Account Type</Form.Label>
            <Form.Control
              as="select"
              name="account"
              value={formData.account}
              onChange={handleChange}
              required
            >
              <option value="Credit">Credit</option>
              <option value="Debit">Debit</option>
            </Form.Control>
          </Form.Group>
  
          <Form.Group controlId="accountName" className="mb-3">
            <Form.Label>Account Name</Form.Label>
            <Form.Control
              type="text"
              name="accountName"
              value={formData.accountName}
              placeholder="Search account name"
              onChange={handleChange}
              required
            />
            {landlordSearch.length >= 3 && filteredLandlords.length > 0 && (
              <ListGroup className="dropdown-menu mt-2">
                {filteredLandlords.map((landlord) => (
                  <ListGroup.Item
                    key={landlord._id}
                    action
                    onClick={() => handleLandlordSelect(landlord)}
                  >
                    {landlord.name} ({landlord.company || 'No company'})
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Form.Group>
  
          <Form.Group controlId="subAccount" className="mb-3">
            <Form.Label>Sub-Account</Form.Label>
            <Form.Control
              as="select"
              name="subAccount"
              value={formData.subAccount}
              onChange={handleChange}
              required
            >
              <option value="LOAN">LOAN</option>
              <option value="EXPENSE">EXPENSE</option>
              <option value="SERVICES">SERVICES</option>
              <option value="OTHER INCOME">OTHER INCOME</option>
              <option value="PAYMENT">PAYMENT</option>
              <option value="DISCOUNT">DISCOUNT</option>
            </Form.Control>
          </Form.Group>
  
          <Form.Group controlId="details" className="mb-3">
            <Form.Label>Details</Form.Label>
            <Form.Control
              type="text"
              name="details"
              value={formData.details}
              placeholder="Enter details"
              onChange={handleChange}
              required
            />
          </Form.Group>
  
          <Form.Group controlId="amount" className="mb-3">
            <Form.Label>Amount</Form.Label>
            <Form.Control
              type="number"
              name="amount"
              value={formData.amount}
              placeholder="Enter amount"
              onChange={handleChange}
              required
            />
          </Form.Group>
  
          <Form.Group controlId="category" className="mb-3">
            <Form.Label>Category</Form.Label>
            <Form.Control
              as="select"
              name="category"
              value={formData.category}
              onChange={handleChange}
            >
              <option value="SalesCredit">SalesCredit</option>
              <option value="Service">Service</option>
              <option value="Consult">Consult</option>
              <option value="Lab">Lab</option>
              <option value="Electricity">Electricity</option>
             <option value="SalesPayment">SalesPayment</option>
             <option value="Other">Other</option>
             <option value="supplyPayment">SupplyPayment</option>
              <option value="Travel">Travel</option>
              <option value="Food">Food</option>
              <option value="Office Supplies">Office Supplies</option>
            <option value="Observation">Observation</option>
            <option value="Admission">Admission</option>
            <option value="Electricity">Electricity</option>
            <option value="Dressing">Dressing</option>
            <option value="Delivery">Delivery</option>
            <option value="Payment">Payment</option>
                                      
            </Form.Control>
          </Form.Group>
  
          <Form.Group controlId="notes" className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              name="notes"
              value={formData.notes}
              placeholder="Enter additional notes"
              rows={3}
              onChange={handleChange}
            />
          </Form.Group>
          <Form.Group controlId="username" className="mb-3">
            <Form.Label>User</Form.Label>
            <Form.Control
              as="textarea"
              name="notes"
              value={userInfo.name}
              
              rows={3}
              onChange={handleChange}
            />
          </Form.Group>
  
          <Form.Group controlId="images" className="mb-3">
            <Form.Label>Images</Form.Label>
            <Form.Control
              type="file"
              multiple
              onChange={(e) => uploadImageHandler(e.target.files)}
            />
            <div className="mt-3">
              {images.map((img, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'inline-block',
                    position: 'relative',
                    marginRight: '10px',
                  }}
                >
                  <img
                    src={img}
                    alt={`Uploaded ${idx}`}
                    className="img-fluid"
                    style={{ maxWidth: '100px', height: '100px', objectFit: 'cover' }}
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => removeImageHandler(idx)}
                    style={{
                      position: 'absolute',
                      top: '0',
                      right: '0',
                      borderRadius: '50%',
                      padding: '2px 5px',
                    }}
                  >
                    ✖
                  </Button>
                </div>
              ))}
            </div>
          </Form.Group>
  
          <Button type="submit" variant="primary">
            Update Payment
          </Button>
        </Form>
      )}
    </div>
  );
};

export default PaymentEditScreen;